import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { FetchError } from '@atlassian/jira-fetch';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type {
	PrefetchedDataGql,
	FeaturesResponse,
} from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import type { UIFBacklogCachedDataResult } from '@atlassian/jira-software-uif-early-script';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter';
import type { StoreFieldsActions } from '../../../common/hooks/use-store-fields';
import type { Column } from '../../../model/columns/types';
import type { Permissions } from '../../../model/config/permissions/types';
import type { HierarchyNomenclature } from '../../../model/issue-hierarchy/types';
import type { IssueParent } from '../../../model/issue-parent/types';
import type {
	CardList,
	Card,
	CardId,
	CardIdAsObjectKey,
	ConfigProps,
	CardType,
	BacklogStrategy,
	RefreshSource,
	EstimationType,
	GlobalIssueCreateProps,
	Project,
} from '../../../model/types';
import type { Version } from '../../../model/version/types';
import type { JiraPlanDataRestResponse } from '../../../rest/fetch-plan-data/types';
import type { BoardConfigModel } from '../../../services/load-board-config/types';
import type { CardCreateConfig } from '../../../services/load-card-create-config';
import type { CMPCustomFilter } from '../../reducers/entities/custom-filters/types';

// INITIALIZE APP PROPS
export const INITIALIZE_APP_PROPS = 'state.actions.app.INITIALIZE_APP_PROPS' as const;

export type InitializeAppPropsAction = {
	type: typeof INITIALIZE_APP_PROPS;
	payload: ConfigProps;
};

export const initializeAppProps = (payload: ConfigProps): InitializeAppPropsAction => ({
	type: INITIALIZE_APP_PROPS,
	payload,
});

export const LOAD_APP_DATA_REQUEST = 'state.actions.app.LOAD_APP_DATA_REQUEST' as const;

export type LoadAppDataAction = {
	type: typeof LOAD_APP_DATA_REQUEST;
	payload: {
		prefetchedData?: PrefetchedDataGql | null;
		prefetchedAggData?: aggBacklogDataQuery$data | null;
		prefetchedJiraPlanRestData?: JiraPlanDataRestResponse | null;
		prefetchCmpBacklogData?: UIFBacklogCachedDataResult | null;
		storeFieldsActions?: StoreFieldsActions | null;
	};
};

export const loadAppData = (
	payload: {
		prefetchedData?: PrefetchedDataGql | null;
		prefetchedAggData?: aggBacklogDataQuery$data | null;
		prefetchedJiraPlanRestData?: JiraPlanDataRestResponse | null;
		prefetchCmpBacklogData?: UIFBacklogCachedDataResult | null;
		storeFieldsActions?: StoreFieldsActions | null;
	} = {},
): LoadAppDataAction => ({
	type: LOAD_APP_DATA_REQUEST,
	payload,
});

export const LOAD_APP_DATA_FAILURE = 'state.actions.app.LOAD_APP_DATA_FAILURE' as const;

export type LoadAppDataFailureAction = {
	type: typeof LOAD_APP_DATA_FAILURE;
};

export const loadAppDataFailure = (): LoadAppDataFailureAction => ({
	type: LOAD_APP_DATA_FAILURE,
});

export const SET_APP_DATA = 'state.actions.app.SET_APP_DATA' as const;

export type ConfigPayload = {
	requestColumnMigration: boolean;
	rankCustomFieldId: number;
	name?: string;
	permissions?: {
		CAN_MANAGE_SPRINTS: boolean;
		CAN_CREATE_ISSUES: boolean;
		CAN_EDIT_ISSUES: boolean;
		CAN_EDIT_BOARD_CONFIG?: boolean;
	};
	version: {
		isVersionsFeatureEnabled: boolean;
	};
	allFeatures: FeaturesResponse[];
	backlogStrategy?: BacklogStrategy;
	storyPointFieldId?: string | null;
	timeTrackingOptions?: TimeTrackingOptions;
	isInlineCardCreateEnabled?: boolean;
	kanbanBoardColumnId?: number | null;
	kanbanBacklogColumnId?: number | null;
	isOrderedByRank?: boolean;
	hierarchyNomenclature?: HierarchyNomenclature | null;
};

export type SetAppDataPayload = {
	entities: {
		card: Record<CardIdAsObjectKey, Card>;
		cardTypes?: CardType[];
		cardOrder: {
			[key: string]: CardId[];
		};
		cardOrderGlobal?: CardId[];
		cardListOrder: string[];
		cardList: {
			[key: string]: CardList;
		};
		column?: { [key: string]: Column };
		issueParents?: {
			[key: string]: IssueParent;
		};
		issueParentsOrder?: string[];
		estimation?: {
			isEstimationFeatureEnabled: boolean;
			estimationType: EstimationType;
			estimationFieldId: string | null;
			name: string;
		};
		project?: {
			projects: Project[];
		};
		versions?: Record<string, Version>;
		version: {
			versions: string[];
		};
		inlineCreate?: {
			cardTypeIds: number[];
		};
	};
	config: ConfigPayload;
	ui?: {
		cards?: {
			filteredCards?: {
				isLoading: boolean;
				filteredCardsIds: Set<string> | null;
			};
		};
	};
};

export type SetAppDataAction = {
	type: typeof SET_APP_DATA;
	payload: SetAppDataPayload;
};

export const setAppData = (payload: SetAppDataPayload): SetAppDataAction => ({
	type: SET_APP_DATA,
	payload,
});

export const LOAD_CONFIG_DATA_REQUEST = 'state.actions.app.LOAD_CONFIG_DATA_REQUEST' as const;

export type LoadConfigDataAction = {
	type: typeof LOAD_CONFIG_DATA_REQUEST;
};

export const loadConfigData = (): LoadConfigDataAction => ({
	type: LOAD_CONFIG_DATA_REQUEST,
});

export const LOAD_CONFIG_DATA_FAILURE = 'state.actions.app.LOAD_CONFIG_DATA_FAILURE' as const;

export type LoadConfigDataFailureAction = {
	type: typeof LOAD_CONFIG_DATA_FAILURE;
};

export const loadConfigDataFailure = (): LoadConfigDataFailureAction => ({
	type: LOAD_CONFIG_DATA_FAILURE,
});

/**
 * Replaces `LOAD_CONFIG_DATA_SUCCESS`, `SET_CONFIG_DATA`, `SET_CUSTOM_FILTERS`,
 * `LOAD_CARD_CREATE_CONFIG_REQUEST`, `SET_COLUMN` and `SET_ALL_PANELS`
 */
export const LOAD_CONFIG_DATA_MODEL_SUCCESS =
	'state.actions.app.LOAD_CONFIG_DATA_MODEL_SUCCESS' as const;

export type LoadConfigDataModelSuccess = {
	type: typeof LOAD_CONFIG_DATA_MODEL_SUCCESS;
	payload: BoardConfigModel;
};

export const loadConfigDataModelSuccess = (payload: BoardConfigModel) => ({
	type: LOAD_CONFIG_DATA_MODEL_SUCCESS,
	payload,
});

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export const LOAD_CONFIG_DATA_SUCCESS = 'state.actions.app.LOAD_CONFIG_DATA_SUCCESS' as const;

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export type LoadConfigDataSuccessAction = {
	type: typeof LOAD_CONFIG_DATA_SUCCESS;
};

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export const loadConfigDataSuccess = (): LoadConfigDataSuccessAction => ({
	type: LOAD_CONFIG_DATA_SUCCESS,
});

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export const SET_CONFIG_DATA = 'state.actions.app.SET_CONFIG_DATA' as const;

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
type SetConfigDataPayload = {
	entities: {
		estimation: {
			isEstimationFeatureEnabled: boolean;
			estimationType: EstimationType;
			estimationFieldId: string | null;
			name: string;
		};
		tracking?: {
			name: string;
			typeId: string;
			isValid: boolean;
			isEnabled: boolean;
		};
		quickFilters: CMPCustomFilter[];
	};
	config: {
		name?: string;
		permissions?: Partial<Permissions>;
		backlogStrategy?: BacklogStrategy;
		isInlineCardCreateEnabled: boolean;
		sprintFieldId?: string | null;
		storyPointFieldId?: string | null;
		epicIssueType: {
			id: string;
			name: string;
			iconUrl: string;
		} | null;
		epicLinkField: {
			id: string;
			name: string;
		} | null;
		timeTrackingOptions?: {
			workingHoursPerDay: number;
			workingDaysPerWeek: number;
			timeFormat: string;
			defaultUnit: string;
		};
		jqlQuery: string | null;
		isOrderedByRank: boolean;
		isBoardCrossProject: boolean;
		parallelSprints: boolean;
		showEpicAsPanel: boolean;
	};
};

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export type SetConfigDataAction = {
	type: typeof SET_CONFIG_DATA;
	payload: SetConfigDataPayload;
};

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export const setConfigData = (payload: SetConfigDataPayload): SetConfigDataAction => ({
	type: SET_CONFIG_DATA,
	payload,
});

export const BACKLOG_VIEW = 'state.actions.app.BACKLOG_VIEW' as const;

export type BacklogView = {
	type: typeof BACKLOG_VIEW;
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const backlogView = (analyticsEvent: UIAnalyticsEvent): BacklogView => ({
	type: BACKLOG_VIEW,
	meta: {
		analyticsEvent,
	},
});

export const REFRESH_DATA_REQUEST = 'state.actions.app.REFRESH_DATA_REQUEST' as const;

export type RefreshDataRequestAction = {
	type: typeof REFRESH_DATA_REQUEST;
	payload: {
		source: RefreshSource;
		issues?: GlobalIssueCreateProps[];
		storeFieldsActions?: StoreFieldsActions | null;
	};
};

export const refreshDataRequest = (
	source: RefreshSource,
	issues?: GlobalIssueCreateProps[],
	storeFieldsActions?: StoreFieldsActions | null,
): RefreshDataRequestAction => ({
	type: REFRESH_DATA_REQUEST,
	payload: {
		source,
		issues,
		storeFieldsActions,
	},
});

export const REFRESH_DATA_FAILURE = 'state.actions.app.REFRESH_DATA_FAILURE' as const;

export type RefreshDataFailureAction = {
	type: typeof REFRESH_DATA_FAILURE;
};

export const refreshDataFailure = (): RefreshDataFailureAction => ({
	type: REFRESH_DATA_FAILURE,
});

export const CHECK_GLOBAL_ISSUE_CREATE = 'state.actions.app.CHECK_GLOBAL_ISSUE_CREATE' as const;

export type CheckGlobalIssueCreateAction = {
	type: typeof CHECK_GLOBAL_ISSUE_CREATE;
	payload: {
		issues: GlobalIssueCreateProps[];
	};
};

export const checkGlobalIssueCreate = (
	issues: GlobalIssueCreateProps[],
): CheckGlobalIssueCreateAction => ({
	type: CHECK_GLOBAL_ISSUE_CREATE,
	payload: {
		issues,
	},
});

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export const LOAD_CARD_CREATE_CONFIG_REQUEST =
	'state.actions.app.LOAD_CARD_CREATE_CONFIG_REQUEST' as const;

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export interface LoadCardCreateConfigAction {
	type: typeof LOAD_CARD_CREATE_CONFIG_REQUEST;
}

/**
 * @deprecated Use `LOAD_CONFIG_DATA_MODEL_SUCCESS`. Best practices "actions as events" - https://redux.js.org/style-guide/#model-actions-as-events-not-setters
 */
export const loadCardCreateConfig = (): LoadCardCreateConfigAction => ({
	type: LOAD_CARD_CREATE_CONFIG_REQUEST,
});

export const LOAD_CARD_CREATE_CONFIG_SUCCESS =
	'state.actions.app.LOAD_CARD_CREATE_CONFIG_SUCCESS' as const;

export interface LoadCardCreateConfigSuccessAction {
	type: typeof LOAD_CARD_CREATE_CONFIG_SUCCESS;
	payload: CardCreateConfig;
	meta: { analyticsEvent?: UIAnalyticsEvent };
}

export const loadCardCreateConfigSuccess = (
	response: CardCreateConfig,
	analyticsEvent?: UIAnalyticsEvent,
): LoadCardCreateConfigSuccessAction => ({
	type: LOAD_CARD_CREATE_CONFIG_SUCCESS,
	payload: response,
	meta: { analyticsEvent },
});

export const LOAD_CARD_CREATE_CONFIG_FAILURE =
	'state.actions.app.LOAD_CARD_CREATE_CONFIG_FAILURE' as const;

export interface LoadCardCreateConfigFailureAction {
	type: typeof LOAD_CARD_CREATE_CONFIG_FAILURE;
	payload: { error?: FetchError };
	meta: { analyticsEvent?: UIAnalyticsEvent };
}

export const loadCardCreateConfigFailure = (
	error?: FetchError,
	analyticsEvent?: UIAnalyticsEvent,
): LoadCardCreateConfigFailureAction => ({
	type: LOAD_CARD_CREATE_CONFIG_FAILURE,
	payload: { error },
	meta: { analyticsEvent },
});

export type Action =
	| InitializeAppPropsAction
	| LoadAppDataAction
	| LoadAppDataFailureAction
	| SetAppDataAction
	| LoadConfigDataAction
	| LoadConfigDataFailureAction
	| LoadConfigDataSuccessAction
	| SetConfigDataAction
	| BacklogView
	| RefreshDataRequestAction
	| RefreshDataFailureAction
	| CheckGlobalIssueCreateAction
	| LoadCardCreateConfigAction
	| LoadCardCreateConfigSuccessAction
	| LoadCardCreateConfigFailureAction
	| LoadConfigDataModelSuccess;
